
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import BuilderLayout from "@/components/Builder/BuilderLayout.vue";
import Loading from "@/components/Reusable/Loading.vue";
import UnrecoverableErrors from "@/components/UnrecoverableErrors.vue";
import {
  initializeAppWhenInitialModeIsBuilder,
  loadDocumentFromWordPress
} from "@/lib/wordpress/load";
import { documentStore, uiStore } from "@/store/store";
import { NO_DOCUMENT_ID } from "@/store/util";
import { DocumentMeta } from "@/types/document";
import { ClosingConfirmationChosenOption } from "@/types/ui";
import { saveToWordPressWithErrorsLoggedToUiStore } from "@/lib/wordpress/save";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { enableBrowseMode } from "@/lib/browse-mode";
import { listenForNonceUpdates } from "@/util/nonce-broadcast";

@Component({
  components: {
    BuilderLayout,
    Loading,
    UnrecoverableErrors
  }
})
export default class Builder extends Vue {
  broadcastChannel: BroadcastChannel | null = null;
  async beforeMount() {
    if (window.Breakdance.initialMode === "builder") {
      /* can we have some safety to ensure this shit only gets run once */
      const maybeLoadedData = await loadDocumentFromWordPress(
        window.Breakdance.initialDocumentToLoadId
      );
      initializeAppWhenInitialModeIsBuilder(maybeLoadedData);
    }
    this.broadcastChannel = listenForNonceUpdates();
  }

  beforeDestroy() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
  }

  get interfaceState() {
    return uiStore.interfaceState;
  }

  get errorsPresent() {
    return uiStore.unrecoverableErrorMessages.length;
  }

  get currentDocumentMetaId() {
    return documentStore.document.documentMeta.id;
  }

  @Watch("currentDocumentMetaId", { immediate: true })
  handleCurrentDocumentChange(documentId: DocumentMeta["id"]) {
    const titleParts = ["Breakdance"];

    if (documentId !== NO_DOCUMENT_ID) {
      titleParts.push(documentStore.document.documentMeta.titleLabel);
    }

    window.document.title = titleParts.join(" | ");
  }

  get unsavedChangesPresent(): boolean {
    return uiStore.unsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent", { immediate: true })
  handleUnsavedChangesPresentChange(unsavedChangesPresent: boolean) {
    if (unsavedChangesPresent) {
      this.requestClosingConfirmation();
    } else {
      this.removeClosingConfirmation();
    }
  }

  requestClosingConfirmation() {
    uiStore.requestClosingConfirmation({
      subject: "builder",
      confirmationDefinition: {
        title: "You must save your changes first",
        actionText: "Save and continue",
        type: "warning",
        hasCancel: true,
        hasDiscard: false
      }
    });
  }

  removeClosingConfirmation() {
    uiStore.noClosingConfirmationIsNeededFor("builder");
  }

  get confirmationChosenOption(): ClosingConfirmationChosenOption | undefined {
    return uiStore.closingConfirmations.builder?.chosenOption;
  }

  @Watch("confirmationChosenOption", { immediate: true })
  async handleConfirmationChosenOptionChange(
    chosenOption: ClosingConfirmationChosenOption | undefined
  ) {
    if (chosenOption === "discard") {
      this.removeClosingConfirmation();
      enableBrowseMode();
    } else if (chosenOption === "save") {
      uiStore.setClosingConfirmationIsSaving({
        subject: "builder",
        isSaving: true
      });
      pipe(
        await saveToWordPressWithErrorsLoggedToUiStore(),
        fold(
          () => {
            uiStore.setClosingConfirmationIsSaving({
              subject: "builder",
              isSaving: false
            });
          },
          () => {
            this.removeClosingConfirmation();
            enableBrowseMode();
          }
        )
      );
    }
  }
}
